import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import ProfileChip from "./ProfileChip";
import { useFavorites } from "./FavoritesContext";
import "./CommunityShowcase.css";


import {
  FaArrowRight,
  FaBars,
  FaFacebookF,
  FaInstagram,
  FaMicrophone,
  FaHeart,
  FaMapMarkerAlt,
  FaRegHeart,
  FaSearch,
  FaShareAlt,
  FaGlobe,
  FaThLarge,
  FaTimes,
  FaUser,
  FaBell,
} from "react-icons/fa";

import logo from "../../logo.png";

import {
  useBusinessSearch,
  businessCategoryName,
  businessDisplayName,
} from "./useBusinessSearch";

import { useAuthUser } from "./useAuthUser";
import {
  getNotifications,
  archiveNotification,
} from "./notificationApi";

import "./header.css";

const NAV_LINKS = [
  { to: "/hub", label: "Hub" },
  { to: "/HubMart", label: "HubMart" },
  { to: "/connect", label: "Connect" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

function Header({
  onSearch,
  onSearchSubmit,
  enableSidebarToggle = false,
  enableHeaderSearch = false,
  /* Site language: pass a control (e.g. <LanguageDropdown />) and the
     header shows a compact "globe + EN" button that opens it. Pages
     without a language setting simply omit these. */
  languageControl = null,
  languageLabel = "EN",
}) {
  const location = useLocation();
  const navigate = useNavigate();

 const drawerRef = useRef(null);
const searchBarRef = useRef(null);
const searchPanelRef = useRef(null);

const notificationBtnRef = useRef(null);
const notificationPanelRef = useRef(null);

const langBtnRef = useRef(null);
const langPanelRef = useRef(null);

  /* =========================================================
     SIGNED-IN ACCOUNT
     ========================================================= */

  const { isSignedIn, displayName } = useAuthUser();

  const joinLabel = isSignedIn
    ? "Add business"
    : "Join Us / Sign In";

  const firstName = displayName
    ? displayName.split(/\s+/)[0]
    : "";

  const profileInitial = firstName
    ? firstName[0].toUpperCase()
    : "";

  /* =========================================================
     STATE
     ========================================================= */

  const [menuOpen, setMenuOpen] = useState(false);

  const [searchText, setSearchText] =
    useState("");

  const [isListening, setIsListening] =
    useState(false);

  const [searchOpen, setSearchOpen] =
    useState(false);

  const [panelPos, setPanelPos] =
    useState(null);
    const [notificationOpen, setNotificationOpen] =
  useState(false);

const [notificationPos, setNotificationPos] =
  useState(null);

/*
 * UI only for now.
 * Later this array will come from the notification API.
 */
const [notifications, setNotifications] =
  useState([]);

const [unreadCount, setUnreadCount] =
  useState(0);

const [notificationsLoading, setNotificationsLoading] =
  useState(false);

const [langOpen, setLangOpen] = useState(false);
const [langPos, setLangPos] = useState(null);

  /* =========================================================
     PAGE STATE
     ========================================================= */

  const pathname =
    location.pathname.toLowerCase();

  const isMarket =
    pathname.includes("/hubmart");

  const isHub =
    pathname === "/hub";

  const recognitionSupported =
    typeof window !== "undefined" &&
    (
      "SpeechRecognition" in window ||
      "webkitSpeechRecognition" in window
    );

  const showSearch =
    !!enableHeaderSearch &&
    !isMarket &&
    (
      typeof onSearch === "function" ||
      typeof onSearchSubmit === "function"
    );

  /* =========================================================
     BUSINESS SEARCH
     ========================================================= */

  const {
    matches: businessMatches,
    state: businessSearchState,
  } = useBusinessSearch(searchText);

  const showSearchPanel =
    showSearch &&
    searchOpen &&
    searchText.trim().length >= 2;

  const primaryCta = useMemo(() => {
    return isMarket
      ? {
          to: "/hub",
          label: "Visit Hub",
        }
      : {
          to: "/HubMart",
          label: "Visit HubMart",
        };
  }, [isMarket]);
const loadNotifications = async () => {
  if (!isSignedIn) {
    setNotifications([]);
    setUnreadCount(0);
    return;
  }

  try {
    setNotificationsLoading(true);

    const data =
      await getNotifications();

    setNotifications(
      Array.isArray(data?.notifications)
        ? data.notifications
        : []
    );

    setUnreadCount(
      Number(data?.unread_count) || 0
    );
  } catch (error) {
    console.warn(
      "Could not load notifications:",
      error
    );

    setNotifications([]);
    setUnreadCount(0);
  } finally {
    setNotificationsLoading(false);
  }
};

useEffect(() => {
  if (!isSignedIn) {
    setNotifications([]);
    setUnreadCount(0);
    return;
  }

  loadNotifications();
}, [isSignedIn]);
  /* =========================================================
     ROUTE CHANGE
     ========================================================= */
useEffect(() => {
  setMenuOpen(false);
  setSearchOpen(false);
  setNotificationOpen(false);
  setLangOpen(false);
}, [location.pathname]);
  /* =========================================================
     OUTSIDE CLICK / ESC
     ========================================================= */

  useEffect(() => {
    const handleOutside = (event) => {
      if (
        drawerRef.current &&
        !drawerRef.current.contains(
          event.target
        )
      ) {
        setMenuOpen(false);
      }

      const insideBar =
        searchBarRef.current &&
        searchBarRef.current.contains(
          event.target
        );

      const insidePanel =
        searchPanelRef.current &&
        searchPanelRef.current.contains(
          event.target
        );

      if (!insideBar && !insidePanel) {
        setSearchOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setSearchOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutside
    );

    document.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutside
      );

      document.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, []);

  /* =========================================================
     SEARCH DROPDOWN POSITION
     ========================================================= */

  useEffect(() => {
    if (!showSearchPanel) {
      return undefined;
    }

    const updatePanelPos = () => {
      if (!searchBarRef.current) {
        return;
      }

      const rect =
        searchBarRef.current.getBoundingClientRect();

      setPanelPos({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width,
      });
    };

    updatePanelPos();

    window.addEventListener(
      "resize",
      updatePanelPos
    );

    window.addEventListener(
      "scroll",
      updatePanelPos,
      true
    );

    return () => {
      window.removeEventListener(
        "resize",
        updatePanelPos
      );

      window.removeEventListener(
        "scroll",
        updatePanelPos,
        true
      );
    };
  }, [
    showSearchPanel,
    searchText,
  ]);
/* =========================================================
   NOTIFICATION DROPDOWN POSITION
   ========================================================= */

useEffect(() => {
  if (
    !notificationOpen ||
    !notificationBtnRef.current
  ) {
    return undefined;
  }

  const updateNotificationPos = () => {
    if (!notificationBtnRef.current) {
      return;
    }

    const rect =
      notificationBtnRef.current.getBoundingClientRect();

    const width = Math.min(
      360,
      window.innerWidth - 24
    );

    const left = Math.max(
      12,
      Math.min(
        rect.right - width,
        window.innerWidth - width - 12
      )
    );

    setNotificationPos({
      top: rect.bottom + 10,
      left,
      width,
    });
  };

  updateNotificationPos();

  window.addEventListener(
    "resize",
    updateNotificationPos
  );

  window.addEventListener(
    "scroll",
    updateNotificationPos,
    true
  );

  return () => {
    window.removeEventListener(
      "resize",
      updateNotificationPos
    );

    window.removeEventListener(
      "scroll",
      updateNotificationPos,
      true
    );
  };
}, [notificationOpen]);

useEffect(() => {
  if (!notificationOpen) {
    return undefined;
  }

  const handleOutsideClick = (event) => {
    const clickedBell =
      notificationBtnRef.current?.contains(
        event.target
      );

    const clickedPanel =
      notificationPanelRef.current?.contains(
        event.target
      );

    if (!clickedBell && !clickedPanel) {
      setNotificationOpen(false);
    }
  };

  const handleEscape = (event) => {
    if (event.key === "Escape") {
      setNotificationOpen(false);
    }
  };

  document.addEventListener(
    "mousedown",
    handleOutsideClick
  );

  document.addEventListener(
    "keydown",
    handleEscape
  );

  return () => {
    document.removeEventListener(
      "mousedown",
      handleOutsideClick
    );

    document.removeEventListener(
      "keydown",
      handleEscape
    );
  };
}, [notificationOpen]);
/* =========================================================
   LANGUAGE POPOVER POSITION + OUTSIDE CLICK
   ========================================================= */

useEffect(() => {
  if (!langOpen || !langBtnRef.current) {
    return undefined;
  }

  const updateLangPos = () => {
    if (!langBtnRef.current) {
      return;
    }

    const rect = langBtnRef.current.getBoundingClientRect();
    const width = Math.min(280, window.innerWidth - 24);
    const left = Math.max(
      12,
      Math.min(rect.right - width, window.innerWidth - width - 12)
    );

    setLangPos({
      top: rect.bottom + 10,
      left,
      width,
    });
  };

  updateLangPos();

  window.addEventListener("resize", updateLangPos);
  window.addEventListener("scroll", updateLangPos, true);

  return () => {
    window.removeEventListener("resize", updateLangPos);
    window.removeEventListener("scroll", updateLangPos, true);
  };
}, [langOpen]);

useEffect(() => {
  if (!langOpen) {
    return undefined;
  }

  const handleOutsideClick = (event) => {
    const clickedButton = langBtnRef.current?.contains(event.target);
    const clickedPanel = langPanelRef.current?.contains(event.target);

    if (!clickedButton && !clickedPanel) {
      setLangOpen(false);
    }
  };

  const handleEscape = (event) => {
    if (event.key === "Escape") {
      setLangOpen(false);
    }
  };

  document.addEventListener("mousedown", handleOutsideClick);
  document.addEventListener("keydown", handleEscape);

  return () => {
    document.removeEventListener("mousedown", handleOutsideClick);
    document.removeEventListener("keydown", handleEscape);
  };
}, [langOpen]);

  /* =========================================================
     SIDEBAR
     ========================================================= */

  const toggleSidebarShell = () => {
    if (
      typeof window !== "undefined"
    ) {
      window.dispatchEvent(
        new Event(
          "goh:toggle-sidebar"
        )
      );
    }
  };

  /* =========================================================
     SEARCH HELPERS
     ========================================================= */

  const pushSearch = (value) => {
    if (
      typeof onSearch === "function"
    ) {
      onSearch(value);
    }
  };

  const submitSearch = (raw) => {
    const query = String(
      raw ?? searchText ?? ""
    ).trim();

    if (!query) {
      return;
    }

    setSearchOpen(false);

    if (
      typeof onSearchSubmit ===
      "function"
    ) {
      onSearchSubmit(query);
    } else if (
      typeof onSearch === "function"
    ) {
      onSearch(query);
    }
  };

  const firstBizText = (...values) => {
    for (const value of values) {
      if (typeof value === "string" && value.trim()) return value.trim();
    }
    return "";
  };

  const { isSaved, toggleSave } = useFavorites();
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  useEffect(() => {
    if (!selectedBusiness) return undefined;
    const onKey = (event) =>
      event.key === "Escape" && setSelectedBusiness(null);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selectedBusiness]);

  const sheetProfilePath = firstBizText(
    selectedBusiness?.profile_path,
    selectedBusiness?.profilePath
  );
  const sheetWebsiteRaw = firstBizText(selectedBusiness?.website);
  const sheetWebsite = sheetWebsiteRaw
    ? /^https?:\/\//i.test(sheetWebsiteRaw)
      ? sheetWebsiteRaw
      : `https://${sheetWebsiteRaw}`
    : "";

  const openBusinessMap = (business) => {
    const direct = firstBizText(business?.map_url, business?.mapUrl);
    if (direct) {
      window.open(direct, "_blank", "noopener");
      return;
    }
    const query =
      firstBizText(business?.address, business?.full_address) ||
      `${businessDisplayName(business)} San Antonio`;
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
      "_blank",
      "noopener"
    );
  };

  const shareBusiness = async (business) => {
    const text = `${businessDisplayName(business)} on GoOurHub`;
    const url = window.location.origin;
    try {
      if (navigator.share) {
        await navigator.share({ title: text, text, url });
        return;
      }
    } catch {
      return;
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      window.alert("Link copied.");
    } catch {
      window.alert(url);
    }
  };

  const openBusinessResult = (business) => {
    /* Card-first everywhere: same details card as the hub,
       community, and category pages. */
    setSearchOpen(false);
    setSelectedBusiness(business);
  };

  const handleSearchChange = (
    event
  ) => {
    const value =
      event.target.value;

    setSearchText(value);
    setSearchOpen(true);
    pushSearch(value);
  };

  const clearSearch = () => {
    setSearchText("");
    setSearchOpen(false);
    pushSearch("");
  };

  /* =========================================================
     VOICE SEARCH
     ========================================================= */

  const handleMicClick = () => {
    const SpeechRecognition =
      window.SpeechRecognition ||
      window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert(
        "Voice search works best in Chrome or another supported browser."
      );

      return;
    }

    const recognition =
      new SpeechRecognition();

    recognition.lang = "en-US";
    recognition.continuous = false;
    recognition.interimResults = true;

    let finalTranscript = "";

    recognition.onstart = () =>
      setIsListening(true);

    recognition.onresult = (
      event
    ) => {
      let transcript = "";

      for (
        let i = event.resultIndex;
        i < event.results.length;
        i += 1
      ) {
        transcript +=
          event.results[i][0]
            .transcript;

        if (
          event.results[i].isFinal
        ) {
          finalTranscript +=
            event.results[i][0]
              .transcript;
        }
      }

      const nextValue =
        transcript.trim();

      setSearchText(nextValue);
      setSearchOpen(true);
      pushSearch(nextValue);
    };

    recognition.onerror = () => {
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);

      const spoken = (
        finalTranscript ||
        searchText ||
        ""
      ).trim();

      if (!spoken) {
        return;
      }

      setSearchText(spoken);
      pushSearch(spoken);
      submitSearch(spoken);
    };

    recognition.start();
  };

  /* =========================================================
     NAVIGATION
     ========================================================= */

  const renderNavLinks = () =>
    NAV_LINKS.map((item) => {
      const active =
        item.to.toLowerCase() ===
        "/hub"
          ? pathname === "/hub"
          : pathname.startsWith(
              item.to.toLowerCase()
            );

      return (
        <Link
          key={item.to}
          to={item.to}
          className={`goh-nav__link ${
            active
              ? "is-active"
              : ""
          }`}
          onClick={() =>
            setMenuOpen(false)
          }
        >
          {item.label}
        </Link>
      );
    });

  /* =========================================================
     COMMUNITY SIDEBAR BUTTON
     ========================================================= */

  const sidebarToggleBtnStyle = {
    width: 46,
    height: 46,
    borderRadius: 16,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      "rgba(255,255,255,0.08)",
    border:
      "1px solid rgba(255,255,255,0.14)",
    color: "#fff",
    cursor: "pointer",
    boxShadow:
      "0 8px 18px rgba(17, 8, 30, 0.16)",
    marginLeft: 12,
    flexShrink: 0,
  };

  /* =========================================================
     MOBILE DRAWER
     ========================================================= */

  const drawer =
    menuOpen &&
    createPortal(
      <div
        id="goh-mobile-drawer"
        className="goh-drawer-wrap"
      >
        <div
          ref={drawerRef}
          className="goh-drawer"
        >
          <div className="goh-drawer__top">
            <span>Menu</span>

            <button
              type="button"
              className="goh-drawer__close"
              onClick={() =>
                setMenuOpen(false)
              }
              aria-label="Close menu"
            >
              <FaTimes />
            </button>
          </div>

          {/* Signed-in profile inside mobile drawer */}
          {isSignedIn && (
            <Link
              to="/profile"
              className="goh-drawer__profile"
              onClick={() =>
                setMenuOpen(false)
              }
            >
              <span
                className="goh-drawer__profile-dot"
                aria-hidden="true"
              >
                {profileInitial || (
                  <FaUser />
                )}
              </span>

              <span className="goh-drawer__profile-text">
                <strong>
                  Hi{" "}
                  {firstName ||
                    "there"}{" "}
                  👋
                </strong>

                <span>
                  View my profile
                </span>
              </span>

              <FaArrowRight
                aria-hidden="true"
              />
            </Link>
          )}

          <nav
            className="goh-nav goh-nav--mobile"
            aria-label="Mobile navigation"
          >
            {renderNavLinks()}
          </nav>

          {/* Mobile drawer actions */}
          <div className="goh-drawer__cta-row">
            <HashLink
              smooth
              to="/partner-signup#joinForm"
              className="goh-btn goh-btn--ghost"
              onClick={() =>
                setMenuOpen(false)
              }
            >
              {joinLabel}
            </HashLink>

            <Link
              to={primaryCta.to}
              className="goh-btn goh-btn--primary"
              onClick={() =>
                setMenuOpen(false)
              }
            >
              {primaryCta.label}
            </Link>
          </div>

          <div className="goh-drawer__social">
            <a
              href="https://www.facebook.com/profile.php?id=61560982405400"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaFacebookF />
              <span>Facebook</span>
            </a>

            <a
              href="https://www.instagram.com/goourhub/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaInstagram />
              <span>Instagram</span>
            </a>
          </div>
        </div>
      </div>,
      document.body
    );

  /* =========================================================
     SEARCH RESULTS PANEL
     ========================================================= */

  const searchPanel =
    showSearchPanel &&
    panelPos &&
    createPortal(
      <div
        ref={searchPanelRef}
        className="goh-search-panel"
        style={{
          top: panelPos.top,
          left: panelPos.left,
          width: panelPos.width,
        }}
        role="region"
        aria-label="Search results"
      >
        {businessSearchState ===
          "loading" && (
          <p className="goh-search-panel__note">
            Searching businesses…
          </p>
        )}

        {businessMatches.length >
          0 && (
          <div className="goh-search-panel__list">
            <span className="goh-search-panel__label">
              Businesses
            </span>

            {businessMatches.map(
              (
                business,
                index
              ) => (
                <button
                  key={
                    business.id ||
                    `${businessDisplayName(
                      business
                    )}-${index}`
                  }
                  type="button"
                  className="goh-search-panel__row"
                  onClick={() =>
                    openBusinessResult(
                      business
                    )
                  }
                >
                  <span className="goh-search-panel__name">
                    {businessDisplayName(
                      business
                    ) ||
                      "Unnamed business"}
                  </span>

                  <span className="goh-search-panel__meta">
                    {businessCategoryName(
                      business
                    )}
                  </span>

                  <FaArrowRight
                    aria-hidden="true"
                  />
                </button>
              )
            )}
          </div>
        )}

        {businessSearchState ===
          "ready" &&
          businessMatches.length ===
            0 && (
            <p className="goh-search-panel__note">
              No business names
              match.
            </p>
          )}

        <button
          type="button"
          className="goh-search-panel__all"
          onClick={() =>
            submitSearch()
          }
        >
          <FaSearch
            aria-hidden="true"
          />

          <span>
            Search all of GoOurHub
            for &ldquo;
            {searchText.trim()}
            &rdquo;
          </span>

          <FaArrowRight
            aria-hidden="true"
          />
        </button>
      </div>,
      document.body
    );
/* =========================================================
   NOTIFICATIONS
   ========================================================= */

const notificationPanel =
  isSignedIn &&
  notificationOpen &&
  notificationPos &&
  createPortal(
    <div
      ref={notificationPanelRef}
      className="goh-notification-panel"
      style={{
        top: notificationPos.top,
        left: notificationPos.left,
        width: notificationPos.width,
      }}
      role="dialog"
      aria-label="Notifications"
    >
      <div className="goh-notification-panel__header">
        <div>
          <strong>Notifications</strong>
          <span>
            Updates from GoOurHub
          </span>
        </div>

        {unreadCount > 0 && (
          <span className="goh-notification-panel__count">
            {unreadCount} new
          </span>
        )}
      </div>

      {notificationsLoading ? (
  <div className="goh-notification-empty">
    <span className="goh-notification-empty__icon">
      <FaBell />
    </span>

    <strong>
      Checking notifications...
    </strong>

    <p>
      Getting your latest GoOurHub updates.
    </p>
  </div>
) : notifications.length === 0 ? (
  <div className="goh-notification-empty">
    <span className="goh-notification-empty__icon">
      <FaBell />
    </span>

    <strong>
      You&apos;re all caught up
    </strong>

    <p>
      New activity and updates will
      appear here.
    </p>
  </div>
) : (
       <div className="goh-notification-list">
  {notifications.map((notification) => (
    <button
      key={notification.id}
      type="button"
      className={`goh-notification-item ${
        !notification.read ? "is-unread" : ""
      }`}
      onClick={async () => {
        try {
          await archiveNotification(
            notification.id
          );

          setNotifications((current) =>
            current.filter(
              (item) =>
                item.id !== notification.id
            )
          );

          if (!notification.read) {
            setUnreadCount((current) =>
              Math.max(0, current - 1)
            );
          }

          setNotificationOpen(false);

          const destination =
            notification.action_url ||
            notification.to;

          if (destination) {
            navigate(destination);
          }
        } catch (error) {
          console.warn(
            "Could not archive notification:",
            error
          );
        }
      }}
    >
      <span className="goh-notification-item__dot" />

      <span className="goh-notification-item__content">
        <strong>
          {notification.title}
        </strong>

        <span>
          {notification.message}
        </span>

        {notification.time && (
          <small>
            {notification.time}
          </small>
        )}
      </span>
    </button>
  ))}
</div>
      )}
    </div>,
    document.body
    

  );
  /* =========================================================
     LANGUAGE POPOVER (portaled: the header clips overflow)
     ========================================================= */

  const langPanel =
    !!languageControl &&
    langOpen &&
    langPos &&
    createPortal(
      <div
        ref={langPanelRef}
        className="goh-lang-panel"
        style={{
          top: langPos.top,
          left: langPos.left,
          width: langPos.width,
        }}
        role="dialog"
        aria-label="Site language"
      >
        <div className="goh-lang-panel__head">
          <strong>Site language</strong>
          <span>Applies across GoOurHub</span>
        </div>

        <div className="goh-lang-panel__body">{languageControl}</div>
      </div>,
      document.body
    );

  /* =========================================================
     HEADER
     ========================================================= */

  return (
    <header
      className={`goh-header ${
        isMarket
          ? "goh-header--market"
          : ""
      }`}
    >
      <div className="goh-header__shell">
        {/* Brand area */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            minWidth: 0,
          }}
        >
          <Link
            to="/"
            className="goh-brand"
            aria-label="GoOurHub home"
          >
            <span className="goh-brand__mark">
              <img
                src={logo}
                alt="GoOurHub logo"
                className="goh-brand__logo"
              />
            </span>

            <span className="goh-brand__copy">
              <strong>
                GoOurHub
              </strong>

              <span>
                {isMarket
                  ? "HubMart marketplace"
                  : isHub
                    ? "Business, community, and local discovery"
                    : "Local discovery made clearer"}
              </span>
            </span>
          </Link>

          {enableSidebarToggle && (
            <button
              type="button"
              className="goh-sidebar-toggle"
              style={
                sidebarToggleBtnStyle
              }
              onClick={
                toggleSidebarShell
              }
              aria-label="Toggle community sidebar"
              title="Toggle community sidebar"
            >
              <FaBars />
            </button>
          )}
        </div>

        {/* Desktop navigation */}
        <nav
          className="goh-nav goh-nav--desktop"
          aria-label="Primary navigation"
        >
          {renderNavLinks()}
        </nav>

        {/* Header actions */}
        <div className="goh-header__actions">
          {/* Site language (only when the page passes a control) */}
          {languageControl && (
            <button
              ref={langBtnRef}
              type="button"
              className={`goh-lang-btn ${langOpen ? "is-open" : ""}`}
              onClick={() => {
                setMenuOpen(false);
                setSearchOpen(false);
                setNotificationOpen(false);
                setLangOpen((current) => !current);
              }}
              aria-expanded={langOpen}
              aria-haspopup="dialog"
              aria-label={`Site language: ${languageLabel}. Change language`}
              title="Change language"
            >
              <FaGlobe aria-hidden="true" />
              <span>{languageLabel}</span>
            </button>
          )}

          {/* Notifications */}
{isSignedIn && (
  <button
    ref={notificationBtnRef}
    type="button"
    className={`goh-notification-btn ${
      notificationOpen
        ? "is-open"
        : ""
    }`}
onClick={() => {
  setMenuOpen(false);
  setSearchOpen(false);
  setLangOpen(false);

  if (!notificationOpen) {
    loadNotifications();
  }

  setNotificationOpen(
    (current) => !current
  );
}}
    aria-label={
      unreadCount > 0
        ? `${unreadCount} unread notifications`
        : "Notifications"
    }
    title="Notifications"
    aria-expanded={notificationOpen}
  >
    <FaBell aria-hidden="true" />

    {unreadCount > 0 && (
      <span className="goh-notification-badge">
        {unreadCount > 9
          ? "9+"
          : unreadCount}
      </span>
    )}
  </button>
)}
          {/* Desktop social */}
          <div
            className="goh-social goh-social--desktop"
            aria-label="Social links"
          >
            <a
              href="https://www.facebook.com/profile.php?id=61560982405400"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              title="Facebook"
            >
              <FaFacebookF />
            </a>

            <a
              href="https://www.instagram.com/goourhub/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              title="Instagram"
            >
              <FaInstagram />
            </a>
          </div>

          {/* Community connect */}
          <Link
            to="/connect"
            className="goh-icon-link"
            aria-label="Community connect"
            title="Community connect"
          >
            <FaThLarge />
          </Link>

          {/* Desktop Add business / Join Us */}
          <HashLink
            smooth
            to="/partner-signup#joinForm"
            className="goh-btn goh-btn--ghost goh-btn--desktop-only"
          >
            {joinLabel}
          </HashLink>

          {/* =================================================
              DESKTOP SIGNED-IN PROFILE:
              S + Hi Samrita
              ================================================= */}
          {isSignedIn && (
            <div className="goh-profile-chip-desktop goh-btn--desktop-only">
              <ProfileChip />
            </div>
          )}

          {/* =================================================
              MOBILE SIGNED-IN PROFILE: compact gold avatar → /profile
              ================================================= */}
          {isSignedIn && (
            <Link
              to="/profile"
              className="goh-avatar-btn"
              aria-label={firstName ? `My profile - ${firstName}` : "My profile"}
              title={firstName ? `Hi ${firstName}` : "My profile"}
            >
              {profileInitial || <FaUser />}
            </Link>
          )}

          {/* Hamburger (mobile) */}
          <button
            type="button"
            className={`goh-menu-btn ${menuOpen ? "is-open" : ""}`}
            onClick={() => {
              setLangOpen(false);
              setNotificationOpen(false);
              setMenuOpen((prev) => !prev);
            }}
            aria-expanded={menuOpen}
            aria-controls="goh-mobile-drawer"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {/* =====================================================
          HEADER SEARCH
          ===================================================== */}

      {showSearch && (
        <div className="goh-header__search-row">
          <div
            className="goh-search"
            role="search"
            ref={searchBarRef}
          >
            <FaSearch
              className="goh-search__icon"
              aria-hidden="true"
            />

            <input
              type="text"
              className="goh-search__input header-search-input"
              placeholder="Search businesses, events, communities…"
              value={searchText}
              onChange={
                handleSearchChange
              }
              onFocus={() =>
                setSearchOpen(true)
              }
              onKeyDown={(event) => {
                if (
                  event.key ===
                  "Enter"
                ) {
                  submitSearch();
                }
              }}
              aria-expanded={
                showSearchPanel
              }
              autoComplete="off"
            />

            {searchText && (
              <button
                type="button"
                className="goh-icon-btn"
                onClick={
                  clearSearch
                }
                aria-label="Clear search"
              >
                <FaTimes />
              </button>
            )}

            {recognitionSupported && (
              <button
                type="button"
                className={`goh-icon-btn ${
                  isListening
                    ? "is-active"
                    : ""
                }`}
                onClick={
                  handleMicClick
                }
                aria-label={
                  isListening
                    ? "Listening"
                    : "Voice search"
                }
                title={
                  isListening
                    ? "Listening"
                    : "Voice search"
                }
              >
                <FaMicrophone />
              </button>
            )}
          </div>
        </div>
      )}

      {drawer}
      {searchPanel}
      {notificationPanel}
      {langPanel}

      {selectedBusiness &&
        createPortal(
          <div
            className="ci-sheetwrap"
            onClick={() => setSelectedBusiness(null)}
          >
            <div
              className="ci-sheet"
              role="dialog"
              aria-modal="true"
              aria-label={businessDisplayName(selectedBusiness)}
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="ci-sheet__x"
                onClick={() => setSelectedBusiness(null)}
                aria-label="Close"
              >
                <FaTimes />
              </button>

              <h3 className="ci-sheet__nm">
                {businessDisplayName(selectedBusiness)}
              </h3>

              {firstBizText(
                selectedBusiness?.address,
                selectedBusiness?.full_address
              ) && (
                <p className="ci-sheet__row">
                  <strong>Address:</strong>{" "}
                  {firstBizText(
                    selectedBusiness?.address,
                    selectedBusiness?.full_address
                  )}
                </p>
              )}

              {firstBizText(
                selectedBusiness?.phoneNumber,
                selectedBusiness?.phone_number,
                selectedBusiness?.phone
              ) && (
                <p className="ci-sheet__row">
                  <strong>Phone:</strong>{" "}
                  {firstBizText(
                    selectedBusiness?.phoneNumber,
                    selectedBusiness?.phone_number,
                    selectedBusiness?.phone
                  )}
                </p>
              )}

              <button
                type="button"
                className="ci-sheet__takeme"
                onClick={() => openBusinessMap(selectedBusiness)}
              >
                <FaMapMarkerAlt /> Take Me There
              </button>

              {sheetProfilePath ? (
                <button
                  type="button"
                  className="ci-sheet__site"
                  onClick={() => {
                    setSelectedBusiness(null);
                    navigate(sheetProfilePath);
                  }}
                >
                  View full profile
                </button>
              ) : (
                sheetWebsite && (
                  <a
                    className="ci-sheet__site"
                    href={sheetWebsite}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {sheetWebsiteRaw}
                  </a>
                )
              )}

              <div className="ci-sheet__acts">
                <button
                  type="button"
                  className={`ci-sheet__act ${
                    isSaved(selectedBusiness?.id) ? "is-on" : ""
                  }`}
                  onClick={() =>
                    toggleSave(
                      selectedBusiness?.id,
                      businessDisplayName(selectedBusiness)
                    )
                  }
                  aria-pressed={isSaved(selectedBusiness?.id)}
                >
                  {isSaved(selectedBusiness?.id) ? <FaHeart /> : <FaRegHeart />}{" "}
                  {isSaved(selectedBusiness?.id) ? "Saved" : "Save"}
                </button>

                <button
                  type="button"
                  className="ci-sheet__act"
                  onClick={() => shareBusiness(selectedBusiness)}
                >
                  <FaShareAlt /> Share
                </button>

                <button
                  type="button"
                  className="ci-sheet__act"
                  onClick={() => setSelectedBusiness(null)}
                >
                  Close
                </button>
              </div>

              <p className="ci-sheet__disc">
                Disclaimer: This listing has not been claimed or verified by
                the business owner. Information provided is based on publicly
                available sources.
              </p>
            </div>
          </div>,
          document.body
        )}
    </header>
  );
}

export default Header;
